// The words and colours the workspace uses, one per status everywhere.
export const STATUS = {
  incomplete: { label: "Draft", color: "default" },
  pending: { label: "Submitted", color: "primary" },
  under_review: { label: "In review", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Refused", color: "error" },
  cancelled: { label: "Cancelled", color: "default" },
  expired: { label: "Expired", color: "default" },
};

// Statuses an agent can set, in the order work moves.
export const AGENT_STATUSES = ["pending", "under_review", "approved", "rejected", "cancelled", "expired"];

export const statusLabel = (status) => STATUS[status]?.label ?? status;

// Whether a status change emails the customer unless the agent says
// otherwise: the outcomes do; the team's own steps (Submitted, In review) do
// not.
const EMAIL_BY_DEFAULT = new Set(["approved", "rejected", "cancelled", "expired"]);
export const emailsByDefault = (status) => EMAIL_BY_DEFAULT.has(status);

// The line a toast shows after a status change.
export const statusSaved = (status, emailed) =>
  `Status set to ${statusLabel(status)}. ${emailed ? "The customer was emailed." : "No email was sent."}`;

export const DAY = 24 * 60 * 60 * 1000;

// Whole days since a date, or null when there is none.
export function waitingDays(date) {
  if (!date) return null;
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(Math.floor((Date.now() - then) / DAY), 0);
}

// "Today", "1 day", "9 days" since a date; "" when there is none.
export function waitingFor(date) {
  const days = waitingDays(date);
  if (days == null) return "";
  if (days === 0) return "Today";
  return days === 1 ? "1 day" : `${days} days`;
}

// How long a case has waited, as a tone: under 3 days is normal, 3 to 6 is
// getting old, 7 or more is late. The words travel with the colour.
export function waitTone(days) {
  if (days == null || days < 3) return "normal";
  return days < 7 ? "slow" : "late";
}

// Calendar dates for the rule-based checks: one number per day (UTC midnight),
// so comparisons never trip over time zones or the clock.
export const calendarDay = (year, month, day) => Date.UTC(year, month - 1, day);

export function today() {
  const now = new Date();
  return calendarDay(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

// "30/06/2031" (how answers arrive) to a calendar day, or null.
export function parseAnswerDate(text) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(text ?? "").trim());
  if (!m) return null;
  const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const value = calendarDay(year, month, day);
  const check = new Date(value);
  return check.getUTCDate() === day && check.getUTCMonth() === month - 1 ? value : null;
}

// A stored DATE to a calendar day. "2031-06-30" (with or without a clock
// time) is taken as written; "2031-06-29T21:00:00.000Z", which is how the API
// serialises local midnight on a server east of UTC, rounds to the nearest
// midnight.
export function parseStoredDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  const plain = /^(\d{4})-(\d{2})-(\d{2})(?:[ T][\d:.]*)?$/.exec(text);
  if (plain) return calendarDay(Number(plain[1]), Number(plain[2]), Number(plain[3]));
  const time = new Date(text).getTime();
  if (Number.isNaN(time)) return null;
  return Math.round(time / DAY) * DAY;
}

// A calendar day plus whole months, clamped to the month's last day
// (31 Aug + 6 months is 28 Feb, not 3 Mar).
export function addMonths(day, months) {
  const d = new Date(day);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + months;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Date.UTC(year, month, Math.min(d.getUTCDate(), last));
}

// A calendar day as DD/MM/YYYY, like every date in the workspace.
export function dayText(day) {
  if (day == null) return "";
  const d = new Date(day);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// A calendar day for reading: "2 Oct 2025". DD/MM/YYYY (dayText) stays for
// values staff copy into an official form.
export function dayLong(day) {
  if (day == null) return "";
  const d = new Date(day);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// An answer for reading: a date answer ("10/12/2026", as answers arrive)
// as "10 Dec 2026"; anything else as it is. DD/MM/YYYY stays only in the
// official form's order, where staff type it into the form (QA nadia-26).
// Without a type (an older API) any DD/MM/YYYY answer is read as a date.
export function answerForReading(value, type) {
  const text = String(value ?? "");
  if (type && type !== "date") return text;
  const day = parseAnswerDate(text);
  return day == null ? text : dayLong(day);
}

// The calendar day of an instant on this computer, for reading: "29 Oct 2026".
export function dayOfInstant(date) {
  if (!date) return "";
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  return dayLong(calendarDay(value.getFullYear(), value.getMonth() + 1, value.getDate()));
}

export function dateTime(date) {
  if (!date) return "";
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  return value.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function dateOnly(date) {
  return dayText(parseStoredDate(date));
}

// A stored DATE for reading: "2 Oct 2025".
export function dateLong(date) {
  return dayLong(parseStoredDate(date));
}

export const money = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(
    Number(amount || 0),
  );

export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// The trip date: an arrival answer ("20/12/2026") or a stored date.
export const travelDay = (value) => parseAnswerDate(value) ?? parseStoredDate(value);

// "20 Dec · in 59 days". soon is true within 14 days (and once it has passed),
// when the agent should hurry. year: always say the year ("20 Dec 2026"),
// as the case screen does (one date style, QA nadia-26).
export function tripWhen(day, now = today(), { year = false } = {}) {
  if (day == null) return null;
  const d = new Date(day);
  const days = Math.round((day - now) / DAY);
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear();
  const date = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${sameYear && !year ? "" : ` ${d.getUTCFullYear()}`}`;
  const when = days === 0 ? "today" : days === 1 ? "tomorrow" : days > 0 ? `in ${days} days` : days === -1 ? "yesterday" : `${-days} days ago`;
  return { date, when, days, soon: days <= 14 };
}
